import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Mail, Send, Loader2, Copy, CheckCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Verwaltet Treuhänder/Viewer-Zugriffe (Shares) einer Firma.
 * Nutzt eigenständig den Backend-Endpoint /my/companies/{id}/shares.
 */
export function SettingsSharesSection({ companyId, isOwner }) {
  const [shares, setShares] = useState([]);
  const [shareEmail, setShareEmail] = useState("");
  const [shareRole, setShareRole] = useState("advisor");
  const [shareSaving, setShareSaving] = useState(false);
  const [shareError, setShareError] = useState("");

  // Invite-State
  const [inviting, setInviting] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [lastInvite, setLastInvite] = useState(null); // { email, dev_magic_link, magic_link_sent }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    apiClient
      .get(`/my/companies/${companyId}/shares`)
      .then((r) => setShares(r.data?.shares || []))
      .catch(() => setShares([]));
    apiClient
      .get(`/companies/${companyId}/invites`)
      .then((r) => setPendingInvites((r.data || []).filter((i) => !i.used_at)))
      .catch(() => setPendingInvites([]));
  }, [companyId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!companyId || !shareEmail) return;
    setShareSaving(true);
    setShareError("");
    try {
      const res = await apiClient.post(`/my/companies/${companyId}/shares`, {
        email: shareEmail,
        role: shareRole,
      });
      setShares(res.data?.shares || []);
      setShareEmail("");
      setShareRole("advisor");
    } catch (err) {
      setShareError(err.response?.data?.detail || "Zugriff konnte nicht hinzugefügt werden.");
    } finally {
      setShareSaving(false);
    }
  };

  const handleSendInvite = async () => {
    if (!companyId || !shareEmail) return;
    setInviting(true);
    setShareError("");
    setLastInvite(null);
    try {
      const res = await apiClient.post(`/companies/${companyId}/invite`, {
        email: shareEmail,
        role: shareRole,
      });
      setLastInvite(res.data);
      // Refresh shares (Email wurde durchs Invite hinzugefügt) + invites
      const [sh, iv] = await Promise.all([
        apiClient.get(`/my/companies/${companyId}/shares`),
        apiClient.get(`/companies/${companyId}/invites`),
      ]);
      setShares(sh.data?.shares || []);
      setPendingInvites((iv.data || []).filter((i) => !i.used_at));
      if (res.data.magic_link_sent) {
        toast.success(`Einladung an ${res.data.email} versendet.`);
      } else {
        toast.warning("Magic-Link erstellt, aber SMTP nicht konfiguriert. Link manuell weitergeben.");
      }
      setShareEmail("");
    } catch (err) {
      setShareError(err.response?.data?.detail || "Einladung konnte nicht versendet werden.");
    } finally {
      setInviting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!lastInvite?.dev_magic_link) return;
    try {
      await navigator.clipboard.writeText(lastInvite.dev_magic_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!companyId) return;
    try {
      await apiClient.delete(`/companies/${companyId}/invites/${inviteId}`);
      setPendingInvites((prev) => prev.filter((i) => i.id !== inviteId));
      toast.success("Einladung widerrufen.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Widerruf fehlgeschlagen.");
    }
  };

  const handleRemove = async (email) => {
    if (!companyId) return;
    setShareSaving(true);
    setShareError("");
    try {
      const res = await apiClient.delete(`/my/companies/${companyId}/shares`, {
        data: { email },
      });
      setShares(res.data?.shares || []);
    } catch (err) {
      setShareError(err.response?.data?.detail || "Zugriff konnte nicht entfernt werden.");
    } finally {
      setShareSaving(false);
    }
  };

  return (
    <Card
      id="section-shares"
      className="p-4 border border-slate-200 bg-white shadow-sm scroll-mt-20"
      data-testid="settings-shares-card"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-slate-700">
          Zugriff für Treuhänder / weitere Benutzer
        </h2>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Sie können entweder direkt eine E-Mail freigeben (User loggt sich per Code an)
        oder eine <strong>Einladung mit Magic-Link</strong> verschicken (Klick = Anmeldung).
      </p>

      {shareError && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {shareError}
        </div>
      )}

      {lastInvite?.dev_magic_link && (
        <div
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2"
          data-testid="last-invite-dev-link"
        >
          <span>
            <strong>Dev-Magic-Link</strong> für {lastInvite.email} (SMTP nicht konfiguriert):
          </span>
          <code className="font-mono text-[11px] truncate flex-1">
            {lastInvite.dev_magic_link}
          </code>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleCopyLink}
            className="h-7 px-2 text-[11px]"
            data-testid="copy-magic-link-button"
          >
            {copied ? <CheckCheck className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </Button>
        </div>
      )}

      <form
        className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4"
        onSubmit={handleAdd}
        data-testid="shares-form"
      >
        <div className="md:col-span-6 space-y-1">
          <Label htmlFor="share-email">E-Mail</Label>
          <Input
            id="share-email"
            type="email"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            data-testid="shares-email-input"
          />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label htmlFor="share-role">Rolle</Label>
          <Select value={shareRole} onValueChange={setShareRole}>
            <SelectTrigger id="share-role" data-testid="shares-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="advisor">Treuhänder</SelectItem>
              <SelectItem value="approver">Freigeber (Zahlungen)</SelectItem>
              <SelectItem value="viewer">Nur Lesen</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 flex items-end">
          <Button
            type="submit"
            size="sm"
            variant="outline"
            className="w-full rounded-full"
            disabled={shareSaving || !isOwner}
            data-testid="shares-add-button"
          >
            {shareSaving ? "…" : "Freigeben"}
          </Button>
        </div>
        <div className="md:col-span-2 flex items-end">
          <Button
            type="button"
            size="sm"
            className="w-full rounded-full bg-slate-900 text-slate-50"
            onClick={handleSendInvite}
            disabled={inviting || !isOwner || !shareEmail}
            data-testid="shares-invite-button"
          >
            {inviting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>
                <Send className="h-3.5 w-3.5 mr-1" /> Einladen
              </>
            )}
          </Button>
        </div>
      </form>

      <Table data-testid="shares-table">
        <TableHeader>
          <TableRow>
            <TableHead>E-Mail</TableHead>
            <TableHead>Rolle</TableHead>
            <TableHead className="w-24 text-right">Aktion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shares.length === 0 && (
            <TableRow data-testid="shares-empty-row">
              <TableCell
                colSpan={3}
                className="text-center text-sm text-slate-400"
                data-testid="shares-empty-state"
              >
                Noch keine weiteren Benutzer mit Zugriff.
              </TableCell>
            </TableRow>
          )}
          {shares.map((share) => (
            <TableRow
              key={`${share.email}-${share.role}`}
              data-testid={`shares-row-${share.email}`}
            >
              <TableCell>{share.email}</TableCell>
              <TableCell>
                {share.role === "advisor" ? "Treuhänder" : share.role === "approver" ? "Freigeber" : "Leser"}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemove(share.email)}
                  disabled={!isOwner}
                  data-testid={`shares-remove-button-${share.email}`}
                >
                  Entfernen
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {pendingInvites.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100" data-testid="pending-invites-section">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-3.5 w-3.5 text-slate-500" />
            <h3 className="text-xs font-medium text-slate-700">
              Offene Einladungen ({pendingInvites.length})
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">E-Mail</TableHead>
                <TableHead className="text-xs w-32">Rolle</TableHead>
                <TableHead className="text-xs">Erstellt</TableHead>
                <TableHead className="text-xs w-24 text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingInvites.map((inv) => (
                <TableRow key={inv.id} data-testid={`pending-invite-${inv.id}`}>
                  <TableCell className="text-xs">{inv.email}</TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="outline" className="text-[10px] h-5">
                      {inv.role === "advisor" ? "Treuhänder" : inv.role === "viewer" ? "Leser" : inv.role === "approver" ? "Freigeber" : inv.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {(inv.created_at || "").substring(0, 10)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 h-7 text-[11px]"
                      onClick={() => handleRevokeInvite(inv.id)}
                      data-testid={`revoke-invite-button-${inv.id}`}
                    >
                      Widerrufen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
